import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { TenantProvider } from './context/TenantContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { ThemeProvider } from './context/ThemeContext';
import { StaffTypesProvider } from './hooks/useStaffTypes';
import Home from './Pages/Home';
import CareRecipients from './Pages/CareRecipients';
import Activities from './Pages/Activities';
import PriceItems from './Pages/PriceItems';
import SiteCustomers from './Pages/SiteCustomers';
import CarePlan from './components/CarePlan';
import Quotations from './components/CareQuotation';
import CarePlanDetail from './components/CarePlanDetail';
import ServicePlanQuoteShell from './components/ServicePlanQuoteShell';
import QuotationToShellRedirect from './components/QuotationToShellRedirect';
import HealthAssessment from './components/HealthAssessment';
import StaffDemandReport from './components/StaffDemandReport';
import StaffPrices from './components/StaffPrices';
import Billing from './Pages/Billing';
import BillingPeriod from './Pages/BillingPeriod';
import GLAccounts from './Pages/GLAccounts';
import UserManagementPage from './Pages/UserManagement';
import BrandingPage from './Pages/Branding';
import Layout from './Layout';
import ProtectedRoute from './components/ProtectedRoute';
import OnboardingPendingRoute from './components/OnboardingPendingRoute';
import MembershipGate from './components/MembershipGate';
import Login from './Pages/login';
import OnboardingCallback from './Pages/OnboardingCallback';
import OnboardingPending from './Pages/OnboardingPending';
import AwaitingApproval from './Pages/AwaitingApproval';
import ResetPassword from './Pages/ResetPassword';
import { RoutePermissionGuard } from './components/PermissionGuard';
import { PERMISSIONS } from './constants/permissions';
import { BILLING_ENABLED } from './constants/featureFlags';

function App() {
    return (
        <Router>
            <ThemeProvider>
            <AuthProvider>
            <TenantProvider>
                <PermissionsProvider>
                <StaffTypesProvider>
                    <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Login />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/onboarding/callback" element={<OnboardingCallback />} />

                <Route element={<OnboardingPendingRoute />}>
                    <Route path="/onboarding/pending" element={<OnboardingPending />} />
                </Route>

                {/* Protected Routes */}
                <Route element={<ProtectedRoute />}>
                    <Route path="/awaiting-approval" element={<AwaitingApproval />} />
                    <Route element={<MembershipGate />}>
                    <Route element={<Layout />}>
                        <Route path="/home" element={<Home />} />
                        
                        <Route path="/care-recipients" element={
                            <RoutePermissionGuard permission={PERMISSIONS.SERVICE_PLAN_READ}>
                                <CareRecipients />
                            </RoutePermissionGuard>
                        } />
                        
                        <Route path="/activities" element={
                            <RoutePermissionGuard permission={PERMISSIONS.ACTIVITY_READ}>
                                <Activities />
                            </RoutePermissionGuard>
                        } />
                        
                        <Route path="/priceList" element={
                            <RoutePermissionGuard permission={PERMISSIONS.PRICE_ITEM_READ}>
                                <PriceItems />
                            </RoutePermissionGuard>
                        } />
                        
                        <Route path="/sites/:siteId/customers" element={
                            <RoutePermissionGuard permission={PERMISSIONS.CLIENT_READ}>
                                <SiteCustomers />
                            </RoutePermissionGuard>
                        } />
                        
                        <Route path="/careplans/:id" element={
                            <RoutePermissionGuard permission={PERMISSIONS.SERVICE_PLAN_READ}>
                                <CarePlan />
                            </RoutePermissionGuard>
                        } />
                        
                        <Route path="/careplans/:id/new" element={
                            <RoutePermissionGuard permission={PERMISSIONS.SERVICE_PLAN_UPDATE}>
                                <CarePlanDetail />
                            </RoutePermissionGuard>
                        } />
                        
                        <Route path="/quotations/:id" element={
                            <RoutePermissionGuard permission={PERMISSIONS.QUOTATION_READ}>
                                <Quotations />
                            </RoutePermissionGuard>
                        } />
                        
                        <Route path="/careplans/:id/:carePlanId" element={
                            <RoutePermissionGuard permission={PERMISSIONS.SERVICE_PLAN_READ}>
                                <ServicePlanQuoteShell />
                            </RoutePermissionGuard>
                        }>
                            <Route index element={null} />
                            <Route path="quote/:Qid" element={null} />
                        </Route>
                        
                        <Route path="/quotation/:id/:dateCreated/:Qid" element={
                            <RoutePermissionGuard permission={PERMISSIONS.QUOTATION_READ}>
                                <QuotationToShellRedirect />
                            </RoutePermissionGuard>
                        } />
                        
                        <Route path="/health-assessment" element={
                            <RoutePermissionGuard permission={PERMISSIONS.ASSESSMENT_READ}>
                                <HealthAssessment />
                            </RoutePermissionGuard>
                        } />
                        
                        <Route path="/staff-demand-report" element={
                            <RoutePermissionGuard permission={PERMISSIONS.STAFF_DEMAND_REPORT_READ}>
                                <StaffDemandReport />
                            </RoutePermissionGuard>
                        } />
                        
                        <Route path="/staff-prices" element={
                            <RoutePermissionGuard permission={PERMISSIONS.STAFF_PRICING_READ}>
                                <StaffPrices />
                            </RoutePermissionGuard>
                        } />

                        {BILLING_ENABLED && (
                            <Route path="/billing" element={
                                <RoutePermissionGuard permission={PERMISSIONS.BILLING_READ}>
                                    <Billing />
                                </RoutePermissionGuard>
                            } />
                        )}

                        {BILLING_ENABLED && (
                            <Route path="/billing/:siteId/:year/:month" element={
                                <RoutePermissionGuard permission={PERMISSIONS.BILLING_READ}>
                                    <BillingPeriod />
                                </RoutePermissionGuard>
                            } />
                        )}

                        {BILLING_ENABLED && (
                            <Route path="/gl-accounts" element={
                                <RoutePermissionGuard permission={PERMISSIONS.BILLING_READ}>
                                    <GLAccounts />
                                </RoutePermissionGuard>
                            } />
                        )}

                        {BILLING_ENABLED && (
                            <Route path="/billing-sheets" element={
                                <RoutePermissionGuard permission={PERMISSIONS.BILLING_READ}>
                                    <GLAccounts />
                                </RoutePermissionGuard>
                            } />
                        )}

                        {BILLING_ENABLED && (
                            <Route path="/billing-groups" element={
                                <RoutePermissionGuard permission={PERMISSIONS.BILLING_READ}>
                                    <GLAccounts />
                                </RoutePermissionGuard>
                            } />
                        )}
                        
                        <Route path="/settings" element={<Navigate to="/home" replace />} />

                        <Route path="/branding" element={
                            <RoutePermissionGuard permission={PERMISSIONS.SETTINGS_READ}>
                                <BrandingPage />
                            </RoutePermissionGuard>
                        } />
                        
                        <Route path="/user-management" element={
                            <RoutePermissionGuard
                                role="Admin"
                                section="User Management"
                            >
                                <UserManagementPage />
                            </RoutePermissionGuard>
                        } />
                    </Route>
                    </Route>
                </Route>
                    </Routes>
                </StaffTypesProvider>
                </PermissionsProvider>
            </TenantProvider>
            </AuthProvider>
            </ThemeProvider>
        </Router>
    );
}

export default App;